/**
 * Sticky Buy Bar Desktop - Fixed Version
 * Ensures AJAX cart drawer works properly
 */
(function() {
  'use strict';

  const MOBILE_BREAKPOINT = 767.98;
  const SCROLL_BUFFER = 100;
  
  class StickyBuyBar {
    constructor() {
      this.stickyBar = document.querySelector('[data-sticky-buy-bar]');
      this.originalBuyButtons = document.querySelector('.buy-desktop');
      this.stickyForm = null;
      this.isVisible = false;
      this.ticking = false;
      this.ajaxCartInitialized = false;
      
      if (!this.stickyBar || !this.originalBuyButtons) {
        console.warn('Sticky Buy Bar: Required elements not found');
        return;
      }
      
      this.init();
    }

    init() {
      if (window.innerWidth <= MOBILE_BREAKPOINT) {
        return;
      }
      
      // Find the sticky form
      this.stickyForm = this.stickyBar.querySelector('.ajax-product-form');
      
      if (!this.stickyForm) {
        console.warn('Sticky Buy Bar: AJAX form not found');
        return;
      }
      
      console.log('Sticky Buy Bar: Initialized');
      this.setupEventListeners();
      this.checkVisibility();
      
      // Initialize AJAX cart for sticky form after a short delay
      // This ensures theme's main cart JS has loaded
      setTimeout(() => {
        this.initializeAjaxCart();
      }, 500);
    }

    /**
     * Initialize AJAX cart for the sticky form
     * This ensures the sticky form behaves exactly like the main form
     */
    initializeAjaxCart() {
      if (this.ajaxCartInitialized) return;
      
      console.log('Sticky Buy Bar: Initializing AJAX cart');
      
      // Check if theme has a global AJAX cart initializer
      if (window.theme && typeof window.theme.initAjaxCart === 'function') {
        window.theme.initAjaxCart(this.stickyForm);
        this.ajaxCartInitialized = true;
        return;
      }
      
      // Fallback: Manual AJAX form handling
      this.setupManualAjaxCart();
    }

    /**
     * Manual AJAX cart setup (fallback)
     * Mimics theme's AJAX cart behavior
     */
    setupManualAjaxCart() {
      const submitButton = this.stickyForm.querySelector('[data-add-to-cart]');
      
      if (!submitButton) {
        console.warn('Sticky Buy Bar: Submit button not found');
        return;
      }
      
      this.stickyForm.addEventListener('submit', (e) => {
        // Only prevent default if we're in AJAX mode
        if (!this.stickyForm.classList.contains('ajax-product-form')) {
          return; // Let form submit normally
        }
        
        e.preventDefault();
        console.log('Sticky Buy Bar: Form submitted via AJAX');
        
        // Get form data
        const formData = new FormData(this.stickyForm);
        
        // Disable button
        submitButton.disabled = true;
        submitButton.classList.add('is-loading');
        
        // Send to cart
        fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        })
        .then(response => response.json())
        .then(data => {
          console.log('Sticky Buy Bar: Item added to cart', data);
          
          // Trigger cart update event (theme will listen to this)
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:item-added', {
              bubbles: true,
              detail: { product: data }
            })
          );
          
          // Alternative event names (different themes use different events)
          document.documentElement.dispatchEvent(
            new CustomEvent('product:added', {
              bubbles: true,
              detail: { product: data }
            })
          );
          
          // Try to open cart drawer directly if available
          this.openCartDrawer();
          
          // Re-enable button
          submitButton.disabled = false;
          submitButton.classList.remove('is-loading');
        })
        .catch(error => {
          console.error('Sticky Buy Bar: Add to cart failed', error);
          submitButton.disabled = false;
          submitButton.classList.remove('is-loading');
          
          // Show error message
          alert('Unable to add item to cart. Please try again.');
        });
      });
      
      this.ajaxCartInitialized = true;
      console.log('Sticky Buy Bar: Manual AJAX cart initialized');
    }

    /**
     * Attempt to open cart drawer
     * Tries multiple methods to ensure compatibility
     */
    openCartDrawer() {
      console.log('Sticky Buy Bar: Attempting to open cart drawer');
      
      // Method 1: Check for theme's cart drawer
      if (window.theme && typeof window.theme.openCart === 'function') {
        window.theme.openCart();
        return;
      }
      
      // Method 2: Look for cart drawer element and open it
      const cartDrawer = document.querySelector('[data-cart-drawer]') || 
                        document.querySelector('.drawer--cart') ||
                        document.querySelector('#CartDrawer');
      
      if (cartDrawer) {
        cartDrawer.classList.add('is-open', 'is-visible');
        document.body.classList.add('drawer-open');
        
        // Trigger drawer open event
        cartDrawer.dispatchEvent(new CustomEvent('drawer:open'));
        return;
      }
      
      // Method 3: Look for cart trigger button and click it
      const cartTrigger = document.querySelector('[data-cart-trigger]') ||
                         document.querySelector('.js-cart-trigger') ||
                         document.querySelector('[href="/cart"]');
      
      if (cartTrigger && cartTrigger.click) {
        cartTrigger.click();
        return;
      }
      
      console.warn('Sticky Buy Bar: Could not find cart drawer');
    }

    setupEventListeners() {
      // Scroll listener
      window.addEventListener('scroll', () => {
        if (!this.ticking) {
          window.requestAnimationFrame(() => {
            this.checkVisibility();
            this.ticking = false;
          });
          this.ticking = true;
        }
      });

      // Resize listener
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          if (window.innerWidth <= MOBILE_BREAKPOINT) {
            this.hide();
          } else {
            this.checkVisibility();
          }
        }, 250);
      });

      // Variant change listener
      document.addEventListener('variant:change', (e) => {
        if (e.detail && e.detail.variant) {
          this.updatePrice(e.detail.variant);
          this.updateFormVariant(e.detail.variant);
        }
      });
    }

    checkVisibility() {
      if (window.innerWidth <= MOBILE_BREAKPOINT) return;

      const rect = this.originalBuyButtons.getBoundingClientRect();
      const isCompletelyOutOfView = rect.bottom < (-SCROLL_BUFFER);

      if (isCompletelyOutOfView && !this.isVisible) {
        this.show();
      } else if (!isCompletelyOutOfView && this.isVisible) {
        this.hide();
      }
    }

    show() {
      this.stickyBar.classList.add('is-visible');
      this.isVisible = true;
    }

    hide() {
      this.stickyBar.classList.remove('is-visible');
      this.isVisible = false;
    }

    updatePrice(variant) {
      const priceContainer = this.stickyBar.querySelector('[data-sticky-price]');
      if (!priceContainer || !variant) return;

      const hasDiscount = variant.compare_at_price > variant.price;
      const formatMoney = (cents) => {
        if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
          return Shopify.formatMoney(cents);
        }
        return '₹' + (cents / 100).toFixed(2);
      };

      let priceHTML = `
        <span class="${hasDiscount ? 'product-price__reduced' : ''}">
          <span class="theme-money">${formatMoney(variant.price)}</span>
        </span>
      `;

      if (hasDiscount) {
        priceHTML += `
          <span class="sticky-buy-bar-desktop__compare theme-money">
            ${formatMoney(variant.compare_at_price)}
          </span>
        `;
      }

      priceContainer.innerHTML = priceHTML;
    }

    updateFormVariant(variant) {
      if (!this.stickyForm) return;
      
      const select = this.stickyForm.querySelector('[data-product-select]');
      if (select) {
        select.value = variant.id;
      }
      
      // Update button state
      const submitButton = this.stickyForm.querySelector('[data-add-to-cart]');
      const buttonText = submitButton?.querySelector('[data-add-to-cart-text]');
      
      if (submitButton) {
        if (variant.available) {
          submitButton.disabled = false;
          if (buttonText) {
            buttonText.textContent = submitButton.getAttribute('data-add-text') || 'Add to Cart';
          }
        } else {
          submitButton.disabled = true;
          if (buttonText) {
            buttonText.textContent = 'Sold Out';
          }
        }
      }
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new StickyBuyBar();
    });
  } else {
    new StickyBuyBar();
  }
  
})();